import React, { useEffect, useMemo, useState } from "react";
import { useHistory, useParams } from "react-router-dom";
import { Col, Divider, Row, Tooltip, Modal, notification, Empty } from "antd";
import {
  PlusOutlined,
  MoreOutlined,
  QuestionCircleFilled,
  UpOutlined,
  DownOutlined,
} from "@ant-design/icons";

import { useGetAdviseeProposals, useGetAdviseeProposal } from "apis/advisee";
import { useFetchConstant } from "apis/constant";

import { buildPNRProposalUri } from "utils/routes";
import {
  constructDisplayPrice,
  constructDisplayNumber,
  getLabelOfConstant,
  convertVariableNameToReadableString,
} from "utils/general";

import Title from "components/Title/Title";

import {
  CardHeaderDescription,
  FieldLabel,
  FieldValue,
  ProposalCard,
  ProposalCardBody,
  SettingContainer,
  ViewMoreBar,
  KeyFieldValue,
  CardTag,
  CardTitle,
} from "../../Proposals/Proposals.styles.js";
import { ScreenSize } from "utils/constants";

const constructBenefitList = (policy) => {
  return [
    {
      label: "Life",
      value: constructDisplayPrice(policy.deathBenefit, false),
      code: "deathBenefit",
    },
    {
      label: "TPD",
      value: constructDisplayPrice(policy.tpdBenefit, false),
      code: "tpdBenefit",
    },
    {
      label: "Old Age Disability",
      value: constructDisplayPrice(policy.oldAgeDisabilityBenefit, false),
      code: "oldAgeDisabilityBenefit",
    },
    {
      label: "Critical Illness",
      value: constructDisplayPrice(policy.ciBenefit, false),
      code: "ciBenefit",
    },
    {
      label: "Early Critical Illness",
      value: constructDisplayPrice(policy.totalEarlyCIBenefit, false),
      code: "totalEarlyCIBenefit",
    },
    {
      label: "Personal Accident",
      value: constructDisplayPrice(policy.personalAccidentBenefit, false),
      code: "personalAccidentBenefit",
    },
    {
      label: "Hospitalise Daily Income",
      value: constructDisplayPrice(policy.dailyBenefit, false),
      code: "dailyBenefit",
    },
    {
      label: "Medical Cover",
      value: constructDisplayPrice(policy.medicalCardYearlyLimitAmt, false),
      code: "medicalCardYearlyLimitAmt",
    },
    {
      label: "CIWP",
      value: `${policy.ciWaiver ? "Yes" : "No"}`,
      code: "ciWaiver",
    },
    {
      label: "TPDWP",
      value: `${policy.tpdWaiver ? "Yes" : "No"}`,
      code: "tpdWaiver",
    },
    {
      label: "Medical Card Lifetime limit Amt",
      value: `${
        policy.medicalCardLifetimeLimitAmt === -100 ||
        policy.medicalCardLifetimeLimitAmt === -1
          ? "Unlimited"
          : constructDisplayPrice(policy.medicalCardLifetimeLimitAmt, false)
      }`,
      code: "medicalCardLifetimeLimitAmt",
    },
  ];
};

const Proposal = ({
  data,
  proposalStatusesConst,
  proposalStatuses,
  benefitType,
}) => {
  const [isCardExpanded, setIsCardExpanded] = useState(false);
  const benefits = constructBenefitList(data.policyData);
  // const addedBenefits = constructBenefitList(data.addedPolicyData);
  const [addedBenefits, setAddedBenefits] = useState(
    constructBenefitList(data.addedPolicyData)
  );

  const addedMonthlyPremium = data.addedPolicyData.monthlyPremium || 0;
  const newYearlyPremium = data.policyData.yearlyPremium;
  const deathWaiverPremiumForSelf = data.policyData.deathWaiverPremiumForSelf;
  const coverRatio =
    data.policyData.deathBenefit / (deathWaiverPremiumForSelf * 12);

  const handleOnExpandBarClick = (e) => {
    e.stopPropagation();
    setIsCardExpanded(!isCardExpanded);
  };

  // const StatusTag = ({ status }) => {
  //   let tagColor =
  //     status === proposalStatusesConst.ARCHIVE.code ? "#4F4F4F" : "#2AC940";

  //   const statusLabel = getLabelOfConstant(status, proposalStatuses);
  //   const isStatusActive = status === proposalStatusesConst.ACTIVE.code;

  //   return isStatusActive ? null : (
  //     <CardTag color={tagColor}>{statusLabel}</CardTag>
  //   );
  // };

  useEffect(() => {
    renderAdditionalDisplay();
  }, [data]);

  const renderAdditionalDisplay = () => {
    if (!data.display || data.display.length === 0) {
      return;
    }

    let displayFields = data.display;
    let buffer = [...addedBenefits];

    buffer = buffer.filter((value) => {
      if (value.label.substring(0, 6) === "Extra-") {
        return true;
      }
      return displayFields.filter((dis) => dis === value.code).length > 0;
    });

    displayFields = displayFields.filter(
      (dis) => buffer.filter((x) => x.code === dis).length === 0
    );

    for (let i = 0; i < displayFields.length; i++) {
      const display = displayFields[i];

      if (display.substring(0, 6) === "Extra-") {
        const title = display.substring(6, display.length);
        const value = data.addedPolicyData?.extraCoverage?.filter(
          (x) => x.label === title
        );

        if (value && value.length > 0) {
          buffer.push({
            label: `${title}`,
            value: value[0].amount
              ? constructDisplayNumber(value[0].amount)
              : 0,
          });
          continue;
        }
      }

      let findValue = data.addedPolicyData[display];
      if (!findValue) {
        continue;
      }

      const descLabel = getLabelOfConstant(display, benefitType);

      buffer.push({
        label: descLabel
          ? descLabel
          : convertVariableNameToReadableString(display),
        value: constructDisplayNumber(findValue),
      });
    }

    setAddedBenefits(buffer);
  };
  return (
    <ProposalCard
      key={data._id}
      title={
        <CardTitle>
          {/* <StatusTag status={data.status} /> */}
          <Row>
            <FieldLabel span={24}>{data.name}</FieldLabel>
            <Col span={24}>
              <CardHeaderDescription>{data.description}</CardHeaderDescription>
            </Col>
          </Row>
        </CardTitle>
      }
      headStyle={{
        background: "#ffff00",
        minHeight: 60,
        overflow: "visible",
        whiteSpace: "normal",
        textOverflow: "clip",
      }}
      bodyStyle={{ padding: 0, background: data?.theme?.bgColor }}
      size="small"
      hoverable
    >
      <ProposalCardBody>
        <div style={{ height: "100%" }}>
          {!!data.premiumSurrendered && (
            <Row style={{ marginBottom: 8 }}>
              <FieldLabel span={12}>Premium Surrenderred</FieldLabel>
              <FieldValue span={12}>
                <KeyFieldValue backgroundColor="#ffffff">
                  {constructDisplayPrice(data.premiumSurrendered, false)}
                </KeyFieldValue>
              </FieldValue>
            </Row>
          )}
          {addedBenefits.map((benefit) => (
            <Row key={benefit.label}>
              <FieldLabel span={12}>{benefit.label}</FieldLabel>
              <FieldValue span={12}>{benefit.value}</FieldValue>
            </Row>
          ))}

          <Row style={{ margin: "8px 0px" }}>
            <FieldLabel span={12}>Premium</FieldLabel>
            <FieldValue span={12}>
              <KeyFieldValue backgroundColor="#d7a301">
                {constructDisplayPrice(addedMonthlyPremium * 12, false)}
                <Tooltip
                  placement="topLeft"
                  title={`${constructDisplayPrice(
                    addedMonthlyPremium,
                    false
                  )} per month`}
                >
                  <QuestionCircleFilled style={{ marginLeft: 4 }} />
                </Tooltip>
              </KeyFieldValue>
            </FieldValue>
          </Row>
        </div>
        {isCardExpanded && (
          <>
            <Divider style={{ margin: "16px 0px" }} />
            <div
              style={{
                fontWeight: "bold",
                textDecoration: "underline",
                marginBottom: 4,
              }}
            >
              New Cover
            </div>

            {benefits.map((benefit) => (
              <Row key={benefit.label}>
                <FieldLabel span={12}>{benefit.label}</FieldLabel>
                <FieldValue span={12}>{benefit.value}</FieldValue>
              </Row>
            ))}
            <Row style={{ margin: "8px 0px" }}>
              <FieldLabel span={12}>Premium</FieldLabel>
              <FieldValue span={12}>
                <KeyFieldValue backgroundColor="#ffff00">
                  {constructDisplayPrice(newYearlyPremium, false)}
                  <Tooltip
                    placement="topLeft"
                    title={`${constructDisplayPrice(
                      newYearlyPremium / 12,
                      false
                    )} per month`}
                  >
                    <QuestionCircleFilled style={{ marginLeft: 4 }} />
                  </Tooltip>
                </KeyFieldValue>
              </FieldValue>
            </Row>
            <Row style={{ marginBottom: 8 }}>
              <FieldLabel span={12}>Cover Ratio</FieldLabel>
              <FieldValue span={12}>
                <FieldLabel>{constructDisplayNumber(coverRatio, 2)}</FieldLabel>
              </FieldValue>
            </Row>

            {data.remarks && (
              <>
                <b>Remarks : </b>
                {data.remarks}
              </>
            )}
          </>
        )}
      </ProposalCardBody>
      <ViewMoreBar onClick={handleOnExpandBarClick}>
        {isCardExpanded ? (
          <>
            Less details
            <UpOutlined style={{ marginLeft: 8, fontSize: 8 }} />
          </>
        ) : (
          <>
            More details
            <DownOutlined style={{ marginLeft: 8, fontSize: 8 }} />
          </>
        )}
      </ViewMoreBar>
    </ProposalCard>
  );
};

const ProposalsList = ({
  insurancesInfo = {},
  currentReporting,
  proposalsData,
}) => {
  console.log("proposalsData", proposalsData);
  // console.log('insurancesInfo', insurancesInfo);
  // console.log("currentReporting", currentReporting);

  const { id: adviseeId } = useParams();
  const history = useHistory();

  const [selectedSettings, setSelectedSettings] = useState();
  const [maxPosition, setMaxPosition] = useState(0);

  const [assignProposalId, setAssignProposalId] = useState();

  const {
    isLoading: isProposalsLoading,
    data: proposals,
  } = useGetAdviseeProposals(adviseeId);

  // console.log("proposals", proposals);
  // console.log("proposalDetails", proposalDetails);

  const {
    isLoading: isBenefitTypeloading,
    selection: benefitType,
  } = useFetchConstant("benefitTypes");

  const {
    isLoading: isProposalStatusesLoading,
    selection: proposalStatuses,
    data: proposalStatusesConst,
  } = useFetchConstant("proposalStatuses");

  const {
    isLoading: isProposalCardThemesLoading,
    selection: proposalCardThemes,
  } = useFetchConstant("proposalCardThemes", "colorCode");

  const isLoading =
    isProposalsLoading ||
    isProposalStatusesLoading ||
    isProposalCardThemesLoading;

  const ExistingCover = (
    <div
      style={{
        border: "2px solid rgba(0,0,0,0.65)",
      }}
    >
      <Row
        justify="center"
        style={{
          fontSize: 22,
          fontWeight: "bold",
          color: "black",
          padding: 12,
          borderBottom: "2px solid rgba(0,0,0,0.65)",
        }}
      >
        Existing Cover
      </Row>

      <div
        style={{
          padding: 12,
          fontWeight: 500,
          color: "black",
          borderBottom: "2px solid rgba(0,0,0,0.65)",
        }}
      >
        <Row justify="space-between" style={{ marginBottom: 12 }}>
          <Col>Death</Col>

          <Col>
            {constructDisplayPrice(
              insurancesInfo.deathBenefit ? insurancesInfo.deathBenefit : 0,
              false
            )}
          </Col>
        </Row>

        <Row justify="space-between" style={{ marginBottom: 12 }}>
          <Col>TPD</Col>

          <Col>
            {constructDisplayPrice(
              insurancesInfo.tpdBenefit ? insurancesInfo.tpdBenefit : 0,
              false
            )}
          </Col>
        </Row>

        <Row justify="space-between" style={{ marginBottom: 12 }}>
          <Col>Critical Illness</Col>

          <Col>
            {constructDisplayPrice(
              insurancesInfo.ciBenefit ? insurancesInfo.ciBenefit : 0,
              false
            )}
          </Col>
        </Row>

        <Row justify="space-between" style={{ marginBottom: 12 }}>
          <Col>Early Critical Illness</Col>

          <Col>
            {constructDisplayPrice(
              insurancesInfo.totalEarlyCIBenefit
                ? insurancesInfo.totalEarlyCIBenefit
                : 0,
              false
            )}
          </Col>
        </Row>

        <Row justify="space-between" style={{ marginBottom: 12 }}>
          <Col>Personal Accident</Col>

          <Col>
            {constructDisplayPrice(
              insurancesInfo.personalAccidentBenefit
                ? insurancesInfo.personalAccidentBenefit
                : 0,
              false
            )}
          </Col>
        </Row>

        <Row justify="space-between" style={{ marginBottom: 12 }}>
          <Col>Medical Coverage</Col>

          {/* <Col>0</Col> */}
        </Row>

        <Row justify="space-between" style={{ marginBottom: 12 }}>
          <Col /* offset={6} */>Room & Board</Col>

          <Col>
            {constructDisplayPrice(
              insurancesInfo.medicalCardRoomAndBoardAmt
                ? insurancesInfo.medicalCardRoomAndBoardAmt
                : 0,
              false
            )}
          </Col>
        </Row>

        <Row justify="space-between" style={{ marginBottom: 12 }}>
          <Col /* offset={6} */>Annual Limit</Col>

          <Col>
            {constructDisplayPrice(
              insurancesInfo.medicalCardYearlyLimitAmt
                ? insurancesInfo.medicalCardYearlyLimitAmt
                : 0,
              false
            )}
          </Col>
        </Row>

        <Row justify="space-between" style={{ marginBottom: 12 }}>
          <Col /* offset={6} */>Lifetime Limit</Col>

          <Col>
            {insurancesInfo.medicalCardLifetimeLimitAmt === -100
              ? "Unlimited"
              : constructDisplayPrice(
                  insurancesInfo.medicalCardLifetimeLimitAmt
                    ? insurancesInfo.medicalCardLifetimeLimitAmt
                    : 0,
                  false
                )}
          </Col>
        </Row>

        <Row justify="space-between" style={{ marginBottom: 12 }}>
          <Col>Premium</Col>

          <Col>
            {constructDisplayPrice(
              insurancesInfo.yearlyPremium ? insurancesInfo.yearlyPremium : 0,
              false
            )}
          </Col>
        </Row>

        <Row justify="space-between">
          <Col>Cover Ratio</Col>

          <Col>
            {constructDisplayPrice(
              currentReporting?.riskRatioData?.naturalDeath?.coverRatio?.value
                ? currentReporting.riskRatioData.naturalDeath.coverRatio.value
                : 0,
              false
            )}
          </Col>
        </Row>
      </div>

      <div
        style={{
          padding: 12,
          fontWeight: 500,
          color: "black",
        }}
      >
        <Row
          justify="center"
          style={{ fontWeight: "bold", fontsize: 16, marginBottom: 12 }}
        >
          Gross Insurance Needs
        </Row>

        <Row justify="space-between" style={{ marginBottom: 12 }}>
          <Col>
            Death for{" "}
            {currentReporting?.grossInsNeeds?.deathCover?.yearsOfCoverage} years
          </Col>

          <Col>
            {constructDisplayPrice(
              currentReporting?.grossInsNeeds?.deathCover?.lifeInsNeeds
                ? currentReporting.grossInsNeeds.deathCover.lifeInsNeeds
                : 0,
              false
            )}
          </Col>
        </Row>

        <Row justify="space-between" style={{ marginBottom: 12 }}>
          <Col>
            TPD for {currentReporting?.grossInsNeeds?.tpd?.yearsOfCoverage}{" "}
            years
          </Col>

          <Col>
            {constructDisplayPrice(
              currentReporting?.grossInsNeeds?.tpd?.lifeInsNeeds
                ? currentReporting.grossInsNeeds.tpd.lifeInsNeeds
                : 0,
              false
            )}
          </Col>
        </Row>

        <Row justify="space-between">
          <Col>
            Critical Illness for{" "}
            {currentReporting?.grossInsNeeds?.ci?.yearsOfCoverage} years
          </Col>

          <Col>
            {constructDisplayPrice(
              currentReporting?.grossInsNeeds?.ci?.lifeInsNeeds
                ? currentReporting.grossInsNeeds.ci.lifeInsNeeds
                : 0,
              false
            )}
          </Col>
        </Row>
      </div>
    </div>
  );

  return (
    <div
      style={{
        width: window.innerWidth < ScreenSize.xl && 1489,
      }}
    >
      {!proposalsData ||
        (proposalsData.length === 0 && (
          <Empty description="Waiting for proposals..." />
        ))}
      {!isLoading && Object.keys(proposalsData).length ? (
        <>
          <div>
            <Row gutter={[24, 24]}>
              <Col span={12}>{ExistingCover}</Col>

              {proposalsData.map((proposal, index) => (
                <Col span={12} key={proposal._id} order={index}>
                  <Proposal data={proposal} benefitType={benefitType} />
                </Col>
              ))}
            </Row>
          </div>
        </>
      ) : null}
    </div>
  );
};

export default ProposalsList;
